import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct, CATEGORY_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import NoteCard from "@/components/app/NoteCard";
import OfferDialog from "@/components/app/OfferDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ChangePasswordCard from "@/components/app/ChangePasswordCard";
import TwoFactorCard from "@/components/app/TwoFactorCard";
import WatchlistManager from "@/components/app/WatchlistManager";
import AssignmentManager from "@/components/app/AssignmentManager";
import DocumentUpload from "@/components/app/DocumentUpload";
import SellerListings from "@/components/app/SellerListings";
import PreapprovalStatusPill from "@/components/app/PreapprovalStatusPill";

const USER_DOC_TYPES = [
  { value: "drivers_license", label: "Driver's License" },
  { value: "proof_of_income", label: "Proof of Income" },
  { value: "proof_of_insurance", label: "Proof of Insurance" },
  { value: "proof_of_residence", label: "Proof of Residence" },
  { value: "ssn_verification", label: "SSN Verification" },
  { value: "additional", label: "Additional Supporting Documents" },
];

const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
  countered: "Countered",
  counter_accepted: "Counter Accepted",
  counter_rejected: "Counter Rejected",
};

const APP_STATUS_LABEL = {
  submitted: "Submitted — awaiting manual credit review",
  credit_pulled: "Manual credit review complete — preparing to publish",
  published: "Published — collecting investor pre-approvals",
  preapproved: "Pre-approved by one or more investors",
  matched: "Matched with a vehicle",
  originated: "Loan originated",
  withdrawn: "Withdrawn",
  closed: "Closed",
};

export default function UserDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favs, setFavs] = useState({ items: [], note_ids: [] });
  const [offers, setOffers] = useState([]);
  const [escrowByOffer, setEscrowByOffer] = useState({});
  const [bids, setBids] = useState([]);
  const [preapprovals, setPreapprovals] = useState([]);
  const [offerNote, setOfferNote] = useState(null);
  const [myApp, setMyApp] = useState({ application: null, preapprovals: [] });

  const loadFavs = () => api.get("/favorites").then((r) => setFavs(r.data));
  const loadOffers = () => api.get("/offers").then((r) => setOffers(r.data.items || []));
  const loadEscrow = () => api.get("/escrow").then((r) => {
    const map = {};
    (r.data || []).forEach((e) => { if (e.deal_kind === "offer") map[e.deal_id] = e; });
    setEscrowByOffer(map);
  }).catch(() => {});
  const loadBids = () => api.get("/loan-bids/mine").then((r) => setBids(r.data.items || [])).catch(() => setBids([]));
  const loadPreapprovals = () => api.get("/preapprovals/mine").then((r) => setPreapprovals(r.data.items || [])).catch(() => setPreapprovals([]));
  const loadMyApp = () => api.get("/applications/mine").then((r) => setMyApp(r.data || { application: null, preapprovals: [] })).catch(() => {});

  useEffect(() => {
    if (!user) return;
    loadFavs(); loadOffers(); loadBids(); loadPreapprovals(); loadEscrow(); loadMyApp();
  }, [user]);

  const payEscrow = async (offer) => {
    try {
      const r = await api.post(`/offers/${offer.offer_id}/escrow/checkout`,
        { origin_url: window.location.origin });
      if (r.data?.method === "wire_ach") {
        // Backend now returns wire instructions instead of a Stripe URL.
        setWireInfo({ ...r.data, offer_id: offer.offer_id });
        return;
      }
      if (r.data?.url) {
        window.location.href = r.data.url;   // legacy path, no longer used
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch escrow wire instructions");
    }
  };

  const [wireInfo, setWireInfo] = useState(null);
  const payDeposit = async (pa) => {
    try {
      const r = await api.get(`/preapprovals/${pa.preapproval_id}/deposit-instructions`);
      setWireInfo({ ...r.data, preapproval_id: pa.preapproval_id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch wire instructions");
    }
  };

  if (!user) return null;

  const counterOffers = offers.filter((o) => o.status === "countered");
  const history = offers.filter((o) => !["pending", "countered"].includes(o.status));
  const active = offers.filter((o) => o.status === "pending");
  const app = myApp.application;

  const accept = async (id) => {
    try { await api.post(`/offers/${id}/accept-counter`); toast.success("Counter accepted"); loadOffers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (id) => {
    try { await api.post(`/offers/${id}/reject-counter`); toast.success("Counter rejected"); loadOffers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="page-user-dashboard" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-overline text-accent">FinanceLocal · User</div>
          <h1 className="font-display text-4xl mt-2">Welcome, {user.name}</h1>
        </div>
        <div className="flex gap-2">
          {!app && (
            <Button onClick={() => navigate("/apply")} className="rounded-none uppercase text-xs tracking-wider" data-testid="user-apply-cta">
              Apply for Loan
            </Button>
          )}
          <Button onClick={() => navigate("/notes")} variant="outline" className="rounded-none uppercase text-xs tracking-wider" data-testid="user-browse-cta">
            Browse Notes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 border border-border grid-borders mb-10" data-testid="dashboard-stats">
        <Tile label="Loan App" value={app ? "Active" : "None"} />
        <Tile label="Offers" value={offers.length} />
        <Tile label="Counter Offers" value={counterOffers.length} highlight />
        <Tile label="Pre-Approvals" value={preapprovals.length} />
        <Tile label="Favorites" value={favs.items.length} />
      </div>

      <Tabs defaultValue={app ? "loanapp" : "buy"} className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-border w-full justify-start h-auto p-0 flex-wrap">
          <TabsTrigger value="loanapp" data-testid="tab-loanapp" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">My Loan App</TabsTrigger>
          <TabsTrigger value="buy" data-testid="tab-buy" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Buy Notes</TabsTrigger>
          <TabsTrigger value="sell" data-testid="tab-sell" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Sell Notes</TabsTrigger>
          <TabsTrigger value="counters" data-testid="tab-counters" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Counters {counterOffers.length > 0 && `(${counterOffers.length})`}</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Favorites</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">History</TabsTrigger>
          <TabsTrigger value="bids" data-testid="tab-bids" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Origination Bids {bids.length > 0 && `(${bids.length})`}</TabsTrigger>
          <TabsTrigger value="preapprovals" data-testid="tab-preapprovals" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Pre-Approvals {preapprovals.length > 0 && `(${preapprovals.length})`}</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Transfers</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Alerts</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="loanapp" className="pt-6" data-testid="panel-loanapp">
          {!app ? (
            <div className="border border-border p-10 text-center" data-testid="no-application">
              <div className="font-display text-2xl">No application on file</div>
              <p className="text-muted-foreground mt-2 text-sm">Submit a loan application to receive pre-approval offers from investors and dealers.</p>
              <Button onClick={() => navigate("/apply")} className="rounded-none uppercase text-xs tracking-wider mt-6" data-testid="apply-now-btn">Apply Now</Button>
            </div>
          ) : (
            <>
              <div className="border border-border p-6 mb-6" data-testid="application-status">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-overline text-muted-foreground">Application Status</div>
                    <div className="font-display text-2xl mt-1">{APP_STATUS_LABEL[app.status] || app.status}</div>
                  </div>
                  {(app.status === "submitted" || app.status === "draft") && (
                    <div className="flex gap-2">
                      <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider"
                        onClick={() => navigate("/apply")} data-testid="app-edit-btn">Edit / Withdraw</Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card label="Max Budget" value={fmtMoney(app.vehicle_preferences?.max_budget)} />
                <Card label="Preferred Body" value={app.vehicle_preferences?.body_type || "—"} />
                <Card label="Credit Tier" value={app.derived_profile?.credit_tier || "Pending"} />
                <Card label="Pre-Approvals Offered" value={myApp.preapprovals.length} highlight />
              </div>
              <div className="font-display text-xl mb-3">Offers from Investors / Dealers</div>
              {myApp.preapprovals.length === 0 ? (
                <div className="border border-border p-10 text-center text-muted-foreground" data-testid="no-offers-yet">
                  No pre-approvals yet. Investors will appear here once they commit.
                </div>
              ) : (
                <div className="border border-border" data-testid="my-application-preapprovals">
                  {myApp.preapprovals
                    .slice()
                    .sort((a, b) => (b.max_amount || 0) - (a.max_amount || 0))
                    .map((p) => (
                      <div key={p.preapproval_id} className="border-b border-border last:border-b-0 p-5 grid grid-cols-2 md:grid-cols-5 gap-3 items-center" data-testid={`app-preapproval-${p.preapproval_id}`}>
                        <Stat label="Max Amount" value={fmtMoney(p.max_amount)} highlight />
                        <Stat label="Max APR" value={fmtPct(p.max_apr)} />
                        <Stat label="Term" value={`${p.max_term} ${p.payment_frequency}`} />
                        <Stat label="Status" value={<PreapprovalStatusPill status={p.status} />} />
                        <Stat label="Expires" value={p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"} />
                      </div>
                  ))}
                </div>
              )}

              <div className="border border-border p-6 mt-6" data-testid="user-app-documents">
                <DocumentUpload
                  entityType="loan_application"
                  entityId={app.application_id}
                  title="Supporting Documents"
                  docTypes={USER_DOC_TYPES}
                />
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="buy" className="pt-6" data-testid="panel-buy">
          {offers.length === 0 ? <Empty>You have not submitted any offers. Browse <a className="underline" href="/notes">Available Notes</a> to get started.</Empty> :
            <OfferTable offers={active.concat(counterOffers)} escrowByOffer={escrowByOffer} payEscrow={payEscrow} />}
        </TabsContent>

        <TabsContent value="sell" className="pt-6" data-testid="panel-sell">
          <SellerListings onMessagesClick={(offerId) => navigate(`/messages?offer_id=${offerId}`)} />
        </TabsContent>

        <TabsContent value="counters" className="pt-6">
          {counterOffers.length === 0 ? <Empty>No counter offers yet.</Empty> : (
            <div className="space-y-4" data-testid="counter-offers-list">
              {counterOffers.map((o) => (
                <CounterCard key={o.offer_id} offer={o} onAccept={() => accept(o.offer_id)} onReject={() => reject(o.offer_id)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites" className="pt-6">
          {favs.items.length === 0 ? <Empty>No favorites yet.</Empty> : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="favorites-list">
              {favs.items.map((n) => <NoteCard key={n.note_id} note={n} onMakeOffer={setOfferNote} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="pt-6">
          {history.length === 0 ? <Empty>No completed offers yet.</Empty> : <OfferTable offers={history} escrowByOffer={escrowByOffer} payEscrow={payEscrow} />}
        </TabsContent>

        <TabsContent value="bids" className="pt-6">
          {bids.length === 0 ? (
            <div className="border border-border p-10 text-center text-muted-foreground">
              <div>No origination bids yet.</div>
              <Button onClick={() => navigate("/originations")} className="mt-4 rounded-none uppercase text-xs tracking-wider">Browse Loan Requests</Button>
            </div>
          ) : (
            <div className="border border-border overflow-x-auto" data-testid="my-bids-table">
              <table className="w-full text-sm">
                <thead className="bg-muted/50"><tr className="text-left">
                  <Th>Vehicle</Th><Th>Tier</Th><Th>Rate</Th><Th>Term</Th><Th>Payment</Th><Th>Status</Th><Th></Th>
                </tr></thead>
                <tbody>
                  {bids.map((b) => (
                    <tr key={b.bid_id} className="border-t border-border" data-testid={`bid-row-${b.bid_id}`}>
                      <Td>{b.request ? `${b.request.vehicle.year} ${b.request.vehicle.make} ${b.request.vehicle.model}` : "—"}</Td>
                      <Td className="font-mono-data">{b.request?.buyer_profile?.credit_tier || "—"}</Td>
                      <Td className="font-mono-data text-primary">{b.proposed_interest_rate}%</Td>
                      <Td className="font-mono-data">{b.proposed_term} {b.proposed_payment_frequency}</Td>
                      <Td className="font-mono-data">{fmtMoney(b.computed_payment_amount)}</Td>
                      <Td><span className="font-overline">{b.status}</span></Td>
                      <Td>
                        {b.request && (
                          <Button size="sm" variant="outline" className="rounded-none text-xs h-8"
                            onClick={() => navigate(`/originations/${b.request_id}`)}>View</Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="preapprovals" className="pt-6">
          {preapprovals.length === 0 ? (
            <div className="border border-border p-10 text-center text-muted-foreground">
              <div>No pre-approvals yet.</div>
              <Button onClick={() => navigate("/applications")} className="mt-4 rounded-none uppercase text-xs tracking-wider">Browse Applications</Button>
            </div>
          ) : (
            <div className="border border-border" data-testid="my-preapprovals-list">
              {preapprovals.map((p) => (
                <div key={p.preapproval_id} className="border-b border-border last:border-b-0 p-5 grid grid-cols-2 md:grid-cols-6 gap-3 items-center"
                     data-testid={`preapproval-row-${p.preapproval_id}`}>
                  <Stat label="Max Amount" value={fmtMoney(p.max_amount)} highlight />
                  <Stat label="Max APR" value={`${p.max_apr}%`} />
                  <Stat label="Term" value={`${p.max_term} ${p.payment_frequency}`} />
                  <Stat label="Escrow Deposit" value={fmtMoney(p.deposit?.required_amount)} />
                  <Stat label="Status" value={<PreapprovalStatusPill status={p.status} />} />
                  <div className="flex gap-1 justify-end flex-wrap">
                    {p.status === "awaiting_deposit" && (
                      <Button size="sm" className="rounded-none text-xs h-8"
                        onClick={() => payDeposit(p)} data-testid={`pay-deposit-${p.preapproval_id}`}>Wire Instructions</Button>
                    )}
                    <Button asChild size="sm" variant="outline" className="rounded-none text-xs h-8">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/preapprovals/${p.preapproval_id}/contract.pdf`}
                         target="_blank" rel="noreferrer" data-testid={`contract-pdf-${p.preapproval_id}`}>Contract PDF</a>
                    </Button>
                    {p.awarded_note_id && (
                      <Button asChild size="sm" variant="outline" className="rounded-none text-xs h-8">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/notes/${p.awarded_note_id}/note.pdf`}
                           target="_blank" rel="noreferrer" data-testid={`note-pdf-${p.preapproval_id}`}>Note PDF</a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="transfers" className="pt-6">
          <AssignmentManager scope="mine" />
        </TabsContent>

        <TabsContent value="alerts" className="pt-6">
          <WatchlistManager />
        </TabsContent>

        <TabsContent value="settings" className="pt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-border p-8 space-y-4" data-testid="settings-card">
              <div className="font-overline text-accent">Account</div>
              <div>
                <div className="font-overline text-muted-foreground">Name</div>
                <div className="font-mono-data">{user.name}</div>
              </div>
              <div>
                <div className="font-overline text-muted-foreground">Email</div>
                <div className="font-mono-data">{user.email}</div>
              </div>
              <div>
                <div className="font-overline text-muted-foreground">Role</div>
                <div className="font-mono-data uppercase">{user.role}</div>
              </div>
            </div>
            <ChangePasswordCard />
            <TwoFactorCard />
          </div>
        </TabsContent>
      </Tabs>

      <OfferDialog note={offerNote} open={!!offerNote} onOpenChange={(o) => !o && setOfferNote(null)} onSubmitted={loadOffers} />

      {/* Wire / ACH deposit instructions — Stripe is NEVER used for pre-approval deposits */}
      <Dialog open={!!wireInfo} onOpenChange={(o) => !o && setWireInfo(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="wire-instructions-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {wireInfo?.escrow_id ? "Offer Escrow Wire Instructions" : "Deposit Wire Instructions"}
            </DialogTitle>
          </DialogHeader>
          {wireInfo && (
            <div className="space-y-4 text-sm">
              <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs">
                {wireInfo?.escrow_id
                  ? <>Offer-escrow deposits are transferred via <b>wire or ACH</b> to FinanceLocal&apos;s transaction account. Stripe and credit cards are not accepted.</>
                  : <>Pre-approval deposits are transferred via <b>wire or ACH</b> to FinanceLocal&apos;s transaction account. Stripe and credit cards are not accepted for deposits.</>}
              </div>
              <div className="border border-border p-4 space-y-2 font-mono text-[12px]">
                <Row k="Amount" v={fmtMoney(wireInfo.amount)} highlight />
                <Row k="Reference Code" v={wireInfo.reference_code} highlight />
                <Row k="Beneficiary" v={wireInfo.instructions?.beneficiary_name} />
                <Row k="Bank Name" v={wireInfo.instructions?.bank_name} />
                <Row k="Routing #" v={wireInfo.instructions?.routing_number || "— (contact support)"} />
                <Row k="Account #" v={wireInfo.instructions?.account_number || "— (contact support)"} />
                {wireInfo.instructions?.swift_code && <Row k="SWIFT" v={wireInfo.instructions.swift_code} />}
                <Row k="Wire Memo" v={wireInfo.instructions?.reference_note} />
              </div>
              <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                {(wireInfo.notes || []).map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setWireInfo(null)} data-testid="wire-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, highlight }) {
  return (
    <div className="p-6">
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-3xl mt-2 font-light ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
function Empty({ children }) {
  return <div className="border border-border p-10 text-center text-muted-foreground">{children}</div>;
}
function Card({ label, value, highlight }) {
  return (
    <div className="border border-border p-5">
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
function CounterCard({ offer, onAccept, onReject }) {
  return (
    <div className="border border-border p-6 grid md:grid-cols-[1fr_auto] gap-6" data-testid={`counter-card-${offer.offer_id}`}>
      <div>
        <div className="font-overline text-accent">Counter Offer</div>
        <h3 className="font-display text-xl mt-1">{offer.note?.vehicle_year} {offer.note?.vehicle_make} {offer.note?.vehicle_model}</h3>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div><div className="font-overline text-muted-foreground">Your offer</div><div className="font-mono-data">{fmtMoney(offer.offer_amount)}</div></div>
          <div><div className="font-overline text-muted-foreground">Counter</div><div className="font-mono-data text-primary text-lg">{fmtMoney(offer.counter_amount)}</div></div>
        </div>
        {offer.counter_message && <p className="text-sm text-muted-foreground mt-3">“{offer.counter_message}”</p>}
      </div>
      <div className="flex md:flex-col gap-2">
        <Button onClick={onAccept} className="rounded-none uppercase text-xs tracking-wider" data-testid={`accept-counter-${offer.offer_id}`}>Accept Counter</Button>
        <Button onClick={onReject} variant="outline" className="rounded-none uppercase text-xs tracking-wider" data-testid={`reject-counter-${offer.offer_id}`}>Reject</Button>
      </div>
    </div>
  );
}
function OfferTable({ offers, escrowByOffer = {}, payEscrow }) {
  const navigate = useNavigate();
  const renderEscrow = (o) => {
    const esc = escrowByOffer[o.offer_id];
    const isAccepted = o.status === "accepted" || o.status === "counter_accepted";
    if (esc) {
      const badge = {
        pending: "bg-amber-50 border-amber-200 text-amber-700",
        held: "bg-blue-50 border-blue-200 text-blue-700",
        released: "bg-emerald-50 border-emerald-200 text-emerald-700",
        refunded: "bg-gray-50 border-gray-200 text-gray-700",
      }[esc.status] || "bg-gray-50 border-gray-200 text-gray-700";
      return (
        <span className={`px-2 py-0.5 border text-xs font-overline ${badge}`}
          data-testid={`escrow-badge-${o.offer_id}`}>
          Escrow: {esc.status}
        </span>
      );
    }
    if (isAccepted && payEscrow) {
      return (
        <Button size="sm" className="rounded-none text-xs h-8" onClick={() => payEscrow(o)}
          data-testid={`pay-escrow-${o.offer_id}`}>
          Wire Escrow
        </Button>
      );
    }
    return <span className="text-muted-foreground text-xs">—</span>;
  };
  return (
    <div className="border border-border overflow-x-auto" data-testid="offers-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            <Th>Note</Th><Th>Category</Th><Th>Yield</Th><Th>Your Offer</Th><Th>Counter</Th><Th>Status</Th><Th>Escrow</Th><Th>Thread</Th>
          </tr>
        </thead>
        <tbody>
          {offers.map((o) => (
            <tr key={o.offer_id} className="border-t border-border hover:bg-muted/30" data-testid={`offer-row-${o.offer_id}`}>
              <Td>{o.note ? `${o.note.vehicle_year} ${o.note.vehicle_make} ${o.note.vehicle_model}` : "—"}</Td>
              <Td>{o.note ? CATEGORY_LABELS[o.note.category] : "—"}</Td>
              <Td className="font-mono-data">{o.note ? fmtPct(o.note.estimated_yield) : "—"}</Td>
              <Td className="font-mono-data">{fmtMoney(o.offer_amount)}</Td>
              <Td className="font-mono-data">{o.counter_amount ? fmtMoney(o.counter_amount) : "—"}</Td>
              <Td><span className="font-overline">{STATUS_LABELS[o.status]}</span></Td>
              <Td>{renderEscrow(o)}</Td>
              <Td>
                <Button size="sm" variant="outline" className="rounded-none text-xs h-8"
                  onClick={() => navigate(`/messages?offer_id=${o.offer_id}`)}
                  data-testid={`open-thread-${o.offer_id}`}>Message</Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Row({ k, v, highlight }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-muted-foreground uppercase text-[10px] tracking-wider">{k}</span>
      <span className={`text-right ${highlight ? "text-primary font-semibold" : ""}`}>{v || "—"}</span>
    </div>
  );
}


function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data ${highlight ? "text-primary text-lg" : ""}`}>{value}</div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-overline text-muted-foreground">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
